import { Box, Button, Card, Divider, Stack, Typography } from "@mui/material";
import InputControl from "../../../shared/components/InputControl";
import { Loader, LoadingButton } from "../../../shared";
import { useBrandingConfig } from "../hooks";
import { TenantInfoText } from "../components";

const brandingAssets = [
  {
    key: "logo",
    label: "Logo",
    hint: "Upload a PNG or SVG, recommended 240 x 80 px. Used in the application header and primary branding areas",
  },
  {
    key: "squareLogo",
    label: "Square Logo",
    hint: "Upload a PNG or SVG, recommended 512 x 512 px. Used in square layouts and compact spaces",
  },
  {
    key: "footerLogo",
    label: "Footer Logo",
    hint: "Upload a PNG or SVG, recommended 240 x 80 px. Displayed in the Ordering Website footer",
  },
  {
    key: "favicon",
    label: "Favicon",
    hint: "Upload a PNG, SVG, or ICO, recommended 32 x 32 px. Displayed in Ordering Web browser tabs and bookmarks",
  },
  {
    key: "emailLogo",
    label: "Email Logo",
    hint: "Upload a PNG or SVG, recommended 240 x 80 px. Displayed in system-generated emails",
  },
  {
    key: "posReceiptLogo",
    label: "POS Receipt Logo",
    hint: "Upload a PNG or SVG, recommended 400 x 120 px. Use a simple monochrome design for clear printing on POS receipts",
  },
];

export const BrandingConfig = () => {
  const { state, formValidator, onChange, onAssetChange, onSubmit } =
    useBrandingConfig();

  const validations = {
    logo: formValidator.current.message(
      "Logo",
      state.formData.logo,
      "required",
    ),
    footer_logo: formValidator.current.message(
      "Footer Logo",
      state.formData.footer_logo,
      "required",
    ),
    favicon: formValidator.current.message(
      "Favicon Logo",
      state.formData.favicon,
      "required",
    ),
  };

  if (state.isLoading) return <Loader />;

  return (
    <Card component="form" onSubmit={onSubmit} sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack gap={1.5}>
        <Box>
          <InputControl
            required
            name="name"
            label="Brand Name"
            value={state.formData.name}
            onChange={onChange}
          />
          <TenantInfoText infoKey="brandName" />
        </Box>

        <Stack gap={1.5}>
          {brandingAssets.map(({ key, label, hint }) => {
            const fieldName =
              key === "squareLogo"
                ? "square_logo"
                : key === "footerLogo"
                  ? "footer_logo"
                  : key === "emailLogo"
                    ? "email_logo"
                    : key === "posReceiptLogo"
                      ? "pos_receipt_logo"
                      : key;
            const imageSource =
              state.previews[fieldName] || state.formData[fieldName];

            let accept = "image/png, image/svg+xml";

            if (key === "favicon") {
              accept += ", image/x-icon";
            }

            return (
              <Stack
                key={key}
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                gap={2}
                sx={{
                  minHeight: 54,
                  px: 1,
                  py: 0.75,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "grey.50",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={
                      validations[
                        key === "squareLogo"
                          ? "square_logo"
                          : key === "footerLogo"
                            ? "footer_logo"
                            : key
                      ]
                        ? "error"
                        : "text.primary"
                    }
                  >
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hint}.
                  </Typography>
                  {validations[
                    key === "squareLogo"
                      ? "square_logo"
                      : key === "footerLogo"
                        ? "footer_logo"
                        : key
                  ] && (
                    <Typography variant="caption" color="error">
                      {
                        validations[
                          key === "squareLogo"
                            ? "square_logo"
                            : key === "footerLogo"
                              ? "footer_logo"
                              : key
                        ]
                      }
                    </Typography>
                  )}
                </Box>

                <Stack
                  direction="row"
                  gap={1.5}
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Box
                    sx={{
                      width: 160,
                      height: 72,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                      bgcolor: "#F2F2F2",
                      padding: "10px 12px",
                      overflow: "hidden",
                    }}
                  >
                    {imageSource && (
                      <Box
                        component="img"
                        src={imageSource}
                        alt={`${label} preview`}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </Box>
                  <input
                    id={`${key}-upload`}
                    type="file"
                    accept={accept}
                    onChange={(event) => onAssetChange(event, fieldName)}
                    hidden
                  />
                  <label htmlFor={`${key}-upload`}>
                    <Button
                      component="span"
                      size="small"
                      color="info"
                      sx={{ minWidth: 48, px: "12px" }}
                    >
                      Change
                    </Button>
                  </label>
                </Stack>
              </Stack>
            );
          })}
        </Stack>

        <Divider sx={{ mt: 0.5 }} />
        <LoadingButton
          type="submit"
          variant="contained"
          sx={{ ml: "auto", minWidth: 48 }}
          loading={state.isSubmitting}
          // disabled={!state.hasChanges}
        >
          Save
        </LoadingButton>
      </Stack>
    </Card>
  );
};
